import type { DienstUitzondering } from './dienst-uitzonderingen'
import {
  dayCodeToPloegSoort,
  getShiftForDate,
  type DayCode,
  type PloegSoortValue,
  type ShiftRoster,
} from './shift-roster'

/**
 * FO-348 §3.3 — dienst-slots van een rooster-week: DE dienstdefinitie die de
 * verwacht- en de gepland-kant van de dekking delen.
 *
 * De dekking vergeleek jarenlang twee dingen die niet dezelfde eenheid waren:
 * `verwacht` = "hoeveel diensten draait het rooster deze week" tegenover
 * `gepland` = "op hoeveel kalenderdagen stond íémand van deze klant ingepland".
 * Daardoor telde een dienst die naar een niet-roosterdag verhuisde gewoon door
 * (de dag telde, het lege roosterslot niet), en zag de telling niet of een
 * concreet slot gedekt was.
 *
 * Eén rooster draait per kalenderdag precies één dienst (het rotatiepatroon
 * geeft één code per dag), dus een slot is `(datum × code)`. `verwacht` is
 * daarmee exact het aantal slots — getalsmatig gelijk aan de oude
 * `geldendeDienstenInWeek`-telling voor elk patroon met geldige codes, dus de
 * verwacht-kant verschuift niet. `gepland` telt voortaan de slots uit diezelfde
 * lijst die gevuld zijn. Meerploegige klanten worden per ploeg als eigen rooster
 * gemodelleerd; die krijgen dus hun eigen slots en tellen elkaars bonnen niet mee.
 *
 * Eén nuance t.o.v. de oude telling: een pattern-code buiten `V|L|N|D|X` telde
 * vroeger als dienst (alles wat niet `X` was telde mee) en levert nu geen slot.
 * Zo'n code kan alleen in oude JSON staan — de Zod-validatie laat 'm bij nieuwe
 * writes niet toe — en "onbekende code" als dienst tellen was sowieso een gok.
 *
 * Pure functies, UTC-kalenderdagen — geen DB, geen datum-library.
 */

/** De rooster-velden die bepalen of een rooster een dekking-regel oplevert. */
export interface RoosterRotatieVelden {
  /** Prisma `Json?`; alleen een niet-lege array is een bruikbaar patroon. */
  rotationPattern: unknown
  rotationCycleDays: number | null
  rotationStartDate: Date | null
}

/**
 * FO-348 — levert dit rooster een dekking-regel op?
 *
 * DE bron voor die vraag. Hij wordt op drie plekken gesteld (de engine, de
 * keuzelijst van de fix-actie en de koppel-endpoint) en stond daar eerst in twee
 * dialecten: de engine keek óók naar het patroon, de queries alleen naar
 * cycleDays/startDate. Gevolg: een half geconfigureerd rooster (patroon leeg,
 * de andere twee gevuld) telde voor de engine niet mee, maar de fix-actie zag de
 * bijbehorende bonnen niet - de regel zei "5 dagen", de modal zei "niets meer".
 *
 * Let op: het patroon is `Json?` en dus niet in een Prisma-where te filteren.
 * Query-kant: haal de rotatievelden op en filter hiermee in JS.
 *
 * De lengte-eis (patroonlengte == cycleDays) is óók een crash-guard: een rij
 * met mismatch (bereikbaar via oude onboarding-payloads vóór de
 * werkschema-presets-fix, 2026-07) laat `getShiftForDate` hard gooien — één
 * corrupte rij legde zo de hele dekking-berekening plat, tot in de wekelijkse
 * dekkingsmail en de signalering "niet-geplande diensten". Zo'n rij telt nu
 * gewoon niet mee (zelfde semantiek als half geconfigureerd).
 */
export function heeftDekkingRotatie(rooster: RoosterRotatieVelden): boolean {
  return (
    Array.isArray(rooster.rotationPattern) &&
    rooster.rotationPattern.length > 0 &&
    rooster.rotationCycleDays != null &&
    rooster.rotationPattern.length === rooster.rotationCycleDays &&
    rooster.rotationStartDate != null
  )
}

/** Eén te dekken dienst: een kalenderdag met de rooster-code die hij draait. */
export interface DienstSlot {
  /** Kalenderdag, 'yyyy-mm-dd' — zelfde vorm als de opgeslagen entry-datum. */
  datum: string
  /** De rooster-code van die dag. Nooit 'X' (rust is geen slot). */
  code: Exclude<DayCode, 'X'>
  /** De DB-ploegsoort bij de code — voor leesbare slot-details in de UI. */
  ploeg: PloegSoortValue
  /** Weekdag-index, project-conventie 0=ma … 6=zo. */
  weekdag: number
}

/** Opties voor {@link dienstSlotsInWeek} / {@link geldendeDienstenInWeek}. */
export interface GeldendeDienstenOpties {
  /**
   * Per-week nacht-rooster waarbij de zondagnacht de werkweek inleidt: de
   * LEIDENDE zondag (`maandag - 1`) hoort dan bij DEZE week en de AFSLUITENDE
   * zondag (`maandag + 6`) bij de volgende. Spiegelt `weekStringForShiftDate`
   * en de `zondagnachtAlsStart`-vlag op `ClientWorkSchedule`, zodat elke zondag
   * precies één keer telt - in de week die hij inleidt.
   */
  zondagnachtAlsStart?: boolean
  /** Per-datum uitzonderingen; een `gesloten`-datum telt niet als geldende dienst. */
  uitzonderingen?: DienstUitzondering[]
}

const NL_WEEKDAG_LABEL = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'] as const

/** Kalenderdag `weekMaandag + offset` als UTC-midnight. */
function dagUtc(weekMaandag: Date, offset: number): Date {
  return new Date(
    Date.UTC(
      weekMaandag.getUTCFullYear(),
      weekMaandag.getUTCMonth(),
      weekMaandag.getUTCDate() + offset,
    ),
  )
}

/** 'yyyy-mm-dd' uit de UTC-componenten. */
function ymd(d: Date): string {
  return d.toISOString().slice(0, 10)
}

/**
 * FO-348 — de te dekken dienst-slots in de ISO-week van `weekMaandag`,
 * chronologisch. Vlag- én uitzondering-bewust, exact zoals FO-104 het voor de
 * verwacht-telling deed:
 *  - `dienstUitzonderingen`: een `gesloten`-datum levert geen slot (een tijd-/
 *    pauze-override blijft één slot);
 *  - `zondagnachtAlsStart`: de leidende zondag (`maandag - 1`) hoort bij DEZE
 *    week (en staat dus vooraan), de afsluitende zondag (`maandag + 6`) bij de
 *    volgende - zo telt elke zondag precies één keer, in de week die hij inleidt.
 */
export function dienstSlotsInWeek(
  roster: ShiftRoster,
  weekMaandag: Date,
  opts: GeldendeDienstenOpties = {},
): DienstSlot[] {
  const { zondagnachtAlsStart = false, uitzonderingen = [] } = opts
  const geslotenDatums = new Set(uitzonderingen.filter((u) => u.gesloten).map((u) => u.datum))

  const slotVoor = (offset: number, weekdag: number): DienstSlot | null => {
    const d = dagUtc(weekMaandag, offset)
    const code = getShiftForDate(roster, d)
    if (code === 'X') return null
    const datum = ymd(d)
    if (geslotenDatums.has(datum)) return null
    const ploeg = dayCodeToPloegSoort(code)
    if (!ploeg) return null
    return { datum, code, ploeg, weekdag }
  }

  const slots: DienstSlot[] = []
  // Met de vlag opent de leidende zondag de week; zonder de vlag sluit de
  // zondag hem af. Beide gevallen leveren de lijst chronologisch op.
  if (zondagnachtAlsStart) {
    const leidend = slotVoor(-1, 6)
    if (leidend) slots.push(leidend)
  }
  // ma..za (offsets 0..5) horen altijd bij deze ISO-week.
  for (let i = 0; i < 6; i++) {
    const slot = slotVoor(i, i)
    if (slot) slots.push(slot)
  }
  if (!zondagnachtAlsStart) {
    const afsluitend = slotVoor(6, 6)
    if (afsluitend) slots.push(afsluitend)
  }
  return slots
}

/**
 * FO-104 — aantal DAADWERKELIJK GELDENDE diensten in de ISO-week van
 * `weekMaandag`, vlag- én uitzondering-bewust (i.t.t. het rauwe
 * `verwachteDienstenInWeek`).
 *
 * Sinds FO-348 is dit per definitie het aantal dienst-slots: de verwacht- en de
 * gepland-kant kunnen zo niet meer over een andere dienstdefinitie praten.
 */
export function geldendeDienstenInWeek(
  roster: ShiftRoster,
  weekMaandag: Date,
  opts: GeldendeDienstenOpties = {},
): number {
  return dienstSlotsInWeek(roster, weekMaandag, opts).length
}

/**
 * #36 (dekkingsoverzicht) — aantal verwachte diensten in de ISO-week van
 * `weekMaandag` volgens het roster: tel de niet-`X`-codes over de 7 dagen
 * (ma-zo). Dekt o.a. "5-ploegen vanaf maandag → 6 dagen ma-za" doordat een
 * 5-ploegenrooster doorgaans een `X` op zondag heeft. UTC-kalenderdagen.
 *
 * Dunne wrapper rond {@link geldendeDienstenInWeek} zonder vlaggen/uitzonderingen
 * (gedrag exact gelijk gebleven voor bestaande callers).
 */
export function verwachteDienstenInWeek(roster: ShiftRoster, weekMaandag: Date): number {
  return geldendeDienstenInWeek(roster, weekMaandag)
}

/**
 * FO-348 §3.3 — de gepland-kant: hoeveel van `slots` zijn gedekt?
 *
 * Een slot is gedekt zodra er op die kalenderdag minstens één monteur van dít
 * rooster stond. Wélke monteur is bewust irrelevant (A1): dekking gaat over
 * bezetting, dus een omgeplande of vervangen monteur is géén afwijking zolang
 * het slot gevuld is. Entries op een dag die het rooster niet draait vullen
 * geen slot en tellen dus niet mee - precies het gat dat de oude
 * distinct-dagen-telling openliet.
 */
export function telGevuldeSlots(
  slots: ReadonlyArray<DienstSlot>,
  gevuldeDatums: ReadonlySet<string>,
): number {
  let gevuld = 0
  for (const slot of slots) {
    if (gevuldeDatums.has(slot.datum)) gevuld++
  }
  return gevuld
}

/**
 * FO-348 §4 — leesbare slot-opbouw voor de planner: `"ma D, di D, wo D (leeg)"`.
 * Zo ziet hij in één blik wélk slot open staat i.p.v. alleen "3 van 5".
 */
export function beschrijfSlots(
  slots: ReadonlyArray<DienstSlot>,
  gevuldeDatums: ReadonlySet<string>,
): string {
  return slots
    .map((s) => {
      const dag = NL_WEEKDAG_LABEL[s.weekdag] ?? '?'
      return `${dag} ${s.code}${gevuldeDatums.has(s.datum) ? '' : ' (leeg)'}`
    })
    .join(', ')
}
